import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';

import { formatMetricValue } from './formatMetric';
import { ResolvedMetric } from './metricConfig';

interface Props {
  metrics: ResolvedMetric[];
  currency?: string;
}

export const PerformanceStrip = ({ metrics, currency }: Props) => {
  const { t } = useTranslation();

  if (metrics.length === 0) {
    return null;
  }

  return (
    // Flex, not fixed columns: unrecognised metric keys land here too.
    // rounded-lg matches the KPI tiles above, not the rounded-xl chart panels.
    <div className="flex flex-col divide-y divide-border rounded-lg border border-border bg-background lg:flex-row lg:divide-y-0">
      {metrics.map((metric) => (
        // Pseudo-element, not `lg:divide-x`: the design insets the column rule.
        <div
          className="relative flex flex-1 flex-col items-center gap-1 px-4 py-4 lg:before:absolute lg:before:inset-y-4 lg:before:left-0 lg:before:w-px lg:before:bg-border lg:first:before:hidden"
          key={metric.key}
        >
          <div className="flex items-center gap-1">
            <span className="text-xs leading-4 font-medium text-muted-foreground">
              {t(metric.labelKey)}
            </span>
            {metric.tooltipKey ? (
              <InfoTooltip content={t(metric.tooltipKey)} size="large">
                <InfoIcon className="size-3 shrink-0 text-muted-foreground" />
              </InfoTooltip>
            ) : null}
          </div>
          <span className="text-base leading-6 font-semibold text-foreground">
            {formatMetricValue(metric.value, metric.format, currency)}
          </span>
        </div>
      ))}
    </div>
  );
};
